import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "next/navigation";
import { useRouter } from "next/router";
import PropTypes from "prop-types";
import Suggestion from "./Suggestion";
import {
  APIM_BASE_URL,
  lwAppName,
  SEARCH_RESTRICT_PATTERN,
  searchPLPEndPoint,
  searchSuggestionFL,
} from "@/constants";
import { apim } from "@/constants/api";
import { BsSearch } from "react-icons/bs";
import { useContentfulTranslations } from "@/utils/contentfulTranslations";

export default function HeaderSearch({ setOpenMobileMenu }) {
  const router = useRouter();
  const isSkuSearch = useRef(false);
  const abortControllerRef = useRef(null);
  const suggestionAbortRef = useRef(null);
  const isExplicitSearch = useRef(false);
  const searchParams = useSearchParams();
  const { locale } = router;
  const [searchValue, setSearchValue] = useState("");
  const [suggestionList, setSuggestionList] = useState({});
  const [isSearching, setIsSearching] = useState(false);

  const defaultLocale =
    locale === "default" ? process.env.NEXT_PUBLIC_DEFAULT_LOCALE : locale;

  const apiLocale =
    locale === "default" ? process.env.NEXT_PUBLIC_LW_LANGUAGE : locale;

  const { searchLabel } = useContentfulTranslations();

  const getSuggestions = async (searchTerm, signal) => {
    try {
      const profileName = `profile_${lwAppName}_Typeahead`;
      const query = `q=${searchTerm}&fq=language_s:${encodeURIComponent(apiLocale)}&profilename=${profileName}&fl=${searchSuggestionFL.join(",")}&collections=${lwAppName}`;
      const url = `${APIM_BASE_URL}${searchPLPEndPoint}?${query}`;
      apim
        .get(url, { signal })
        .then(response => {
          if (response?.data) {
            const {
              response: {
                docs = [],
                matchedField = "",
                facet_fields = {},
              } = {},
            } = response.data;
            if (matchedField && facet_fields && facet_fields[matchedField]) {
              matchedField === "sku_s" || matchedField === "CustomerFacingSKU_s"
                ? (isSkuSearch.current = true)
                : (isSkuSearch.current = false);
              // facet_fields[matchedField] is expected to be an array of objects with a 'name' property
              const suggestions = facet_fields[matchedField];
              setSuggestionList(suggestions);
            } else if (docs.length) {
              // fallback to previous behavior
              setSuggestionList(docs);
            }
          }
        })
        .catch(err => {
          // Catch block inside the promise prevents unhandled rejections when aborted
          if (err.name === "CanceledError" || err.name === "AbortError") {
            console.log("Suggestions request cancelled silently.");
          } else {
            console.log(err);
          }
        });
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (isExplicitSearch.current) return;
    const trimmedValue = searchValue.trim();

    // 1. Immediately wipe out suggestions if length conditions aren't met
    if (trimmedValue.length < 3) {
      setSuggestionList({});
      if (suggestionAbortRef.current) {
        suggestionAbortRef.current.abort();
      }
    }

    // 2. Only proceed with setting up the debounce timer if length is valid
    if (trimmedValue.length >= 3) {
      if (suggestionAbortRef.current) {
        suggestionAbortRef.current.abort();
      }

      const controller = new AbortController();
      suggestionAbortRef.current = controller;

      const timeoutId = setTimeout(() => {
        getSuggestions(searchValue, controller.signal);
      }, 500);

      // Clean cleanup function returned safely to React
      return () => {
        clearTimeout(timeoutId);
        controller.abort();
      };
    }

    // Fallback cleanup when query is less than 3 characters
    return () => {
      if (suggestionAbortRef.current) {
        suggestionAbortRef.current.abort();
      }
    };
  }, [searchValue]);

  const isValidSkuFormat = input => {
    // Trims spaces and handles optional K- prefix
    const cleanedInput = input.trim();

    // This matches: K-72760T-CP, 72760T-CP, K-72760, or 72760
    const flexibleKohlerPattern =
      /^(?:K-)?(?:[A-Z]{1,3})?\d{3,6}[A-Z0-9]?(?:-[A-Z0-9]{1,4})*$/i;

    return flexibleKohlerPattern.test(cleanedInput);
  };

  useEffect(() => {
    const handleRouteComplete = () => {
      setIsSearching(false);
      setSuggestionList({});
      isExplicitSearch.current = false;
    };

    // Pages Router syntax shown here. (If using App Router, use usePathname / useSearchParams hooks)
    router.events?.on("routeChangeComplete", handleRouteComplete);
    router.events?.on("routeChangeError", handleRouteComplete);

    return () => {
      router.events?.off("routeChangeComplete", handleRouteComplete);
      router.events?.off("routeChangeError", handleRouteComplete);
    };
  }, [router]);

  // REPLACE YOUR processSearch FUNCTION WITH THIS:
  const processSearch = value => {
    if (!value) return;

    isExplicitSearch.current = true;
    setIsSearching(true);

    setSuggestionList({});

    if (suggestionAbortRef.current) {
      suggestionAbortRef.current.abort();
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Run validation and pass the specific signal downward
    if (isValidSkuFormat(value)) {
      executeSearchAPIAndRedirect(value, true, controller.signal);
    } else {
      setOpenMobileMenu(false);
      setSearchValue("");
      router.push(`/results?search=${encodeURIComponent(value)}`);
    }
  };

  const handleKeyUp = event => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (searchValue.length > 2) processSearch(searchValue);
    }
  };
  const searchOnClick = () => {
    if (searchValue?.length > 2) {
      processSearch(searchValue);
    }
  };
  const handleChange = e => {
    isExplicitSearch.current = false;
    const { target: { value = "" } = {} } = e || {};
    // const sanitizedValue = value.replace(SEARCH_RESTRICT_PATTERN, "");
    setSearchValue(value);
  };

  const isMatched = (result = "", searchTerm = "") =>
    result === searchTerm || result.includes(searchTerm);

  const executeSearchAPIAndRedirect = (
    searchTerm = "",
    ifRedirect = false,
    signal
  ) => {
    const params = new URLSearchParams(searchParams);
    const paramsObj = Object.fromEntries(params.entries());
    const { skuid = "" } = paramsObj || {};
    if (skuid === searchTerm) return false;
    const profileName = `profile_${lwAppName}_Search`;
    const query = `q=${encodeURIComponent(searchTerm)}&fq=language_s:${encodeURIComponent(apiLocale)}&profilename=${profileName}&fl=${searchSuggestionFL.join(",")}&collections=${lwAppName}`;
    const url = `${APIM_BASE_URL}${searchPLPEndPoint}?${query}`;
    apim
      .get(url, { signal })
      .then(response => {
        if (response?.data) {
          const { response: { docs = [] } = {} } = response.data;
          if (docs.length) {
            const [{ slug_s: slug = "", sku_s: sku = "" }] = docs;
            const sanitizedSearchTerm = searchTerm.replace(/^k-/i, "");
            setSuggestionList({});
            if (
              isMatched(sku, sanitizedSearchTerm) &&
              docs.length === 1 &&
              slug
            ) {
              setOpenMobileMenu(false);
              setSearchValue("");
              router.push(`/product-details/${slug}?sku=${sku}`);
            } else if (isMatched(sku, sanitizedSearchTerm) || ifRedirect) {
              setSearchValue("");
              router.push(`/results?search=${searchTerm}`);
            }
          } else {
            setSearchValue("");
            router.push(`/results?search=${searchTerm}`);
          }
        }
      })
      .catch(err => {
        if (err.name === "CanceledError" || err.name === "AbortError") {
          console.log("Direct search request was deliberately canceled.");
        } else {
          console.log(err);
          // Only redirect to fallback results page if it is an actual server error
          setSearchValue("");
          router.push(`/results?search=${searchTerm}`);
        }
      });
  };

  const searchOnSuggestion = (selectedItem = {}, pageRedirection) => {
    const { name = "" } = selectedItem || {};
    if (name) {
      setOpenMobileMenu(false);
      setSearchValue("");
      setSuggestionList({});
      if (isSkuSearch.current) {
        executeSearchAPIAndRedirect(name);
        return false;
      }
      router.push(`/results?search=${name}`);
    }
  };

  return (
    <div className="flex items-center ml-4 w-full" style={{ zIndex: 1000 }}>
      <div className="relative w-full">
        <input
          type="text"
          className="w-full py-2 px-4 pr-10 border-b border-[#ddd] bg-[#f5f5f5] text-[12px] rounded-lg focus:outline-none focus:border-gray-400"
          placeholder={searchLabel?.[defaultLocale] || "Search"}
          onChange={handleChange}
          onKeyUp={handleKeyUp}
          value={searchValue}
          disabled={isSearching}
        />
        <button
          className="cursor-pointer absolute right-0 top-0 bottom-0 px-3 py-2 text-[20px] text-[#bebebe] hover:text-[#232323]"
          onClick={searchOnClick}
          aria-label="Search"
          disabled={isSearching}
        >
          {isSearching ? (
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-[#bebebe] border-t-transparent" />
          ) : (
            <BsSearch size={15} />
          )}
        </button>
        {!isSearching ? (
          <Suggestion
            searchTerm={searchValue}
            list={suggestionList}
            searchOnSuggestion={searchOnSuggestion}
          />
        ) : null}
      </div>
    </div>
  );
}

HeaderSearch.propTypes = {
  setOpenMobileMenu: PropTypes.func,
};

HeaderSearch.defaultProps = {
  setOpenMobileMenu: () => {},
};
